import { getIncident, notifyOwnerIncident, resolveIncident, updateIncident } from '@/lib/api';
import type { IncidentDetail } from '@/lib/api';
import type { OwnerNotificationMethod, UpdateIncidentInput } from '@/lib/types';
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

const OWNER_NOTIFICATION_METHODS: OwnerNotificationMethod[] = [
  'phone',
  'email',
  'sms',
  'whatsapp',
  'in_person',
];

const SEVERITY_COLORS: Record<string, string> = {
  minor: 'bg-green-100 text-green-800',
  moderate: 'bg-amber-100 text-amber-800',
  severe: 'bg-orange-100 text-orange-800',
  critical: 'bg-red-100 text-red-800',
};

function formatDatetime(iso: string): string {
  return new Date(iso).toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function IncidentDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [detail, setDetail] = useState<IncidentDetail | null>(null);
  const incident = detail?.incident ?? null;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [resolving, setResolving] = useState(false);

  // Notify owner panel state
  const [showNotifyPanel, setShowNotifyPanel] = useState(false);
  const [notifyMethod, setNotifyMethod] = useState<OwnerNotificationMethod>('phone');
  const [notifyNotes, setNotifyNotes] = useState('');
  const [notifyAt, setNotifyAt] = useState(() => new Date().toISOString().slice(0, 16));
  const [notifying, setNotifying] = useState(false);

  // Edit panel state (simple inline edit for key fields)
  const [editMode, setEditMode] = useState(false);
  const [editActionTaken, setEditActionTaken] = useState('');
  const [editFollowUpNotes, setEditFollowUpNotes] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    getIncident(id)
      .then((d) => {
        setDetail(d);
        setEditActionTaken(d.incident.actionTaken ?? '');
        setEditFollowUpNotes(d.incident.followUpNotes ?? '');
      })
      .catch((e) => setError((e as Error).message))
      .finally(() => setLoading(false));
  }, [id]);

  async function handleResolve() {
    if (!id || !incident) return;
    if (!confirm('Mark this incident as resolved?')) return;
    setResolving(true);
    try {
      const updated = await resolveIncident(id);
      setDetail((prev) => (prev ? { ...prev, incident: updated } : null));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setResolving(false);
    }
  }

  async function handleNotifyOwner(e: React.FormEvent) {
    e.preventDefault();
    if (!id) return;
    setNotifying(true);
    try {
      const updated = await notifyOwnerIncident(id, {
        method: notifyMethod,
        notes: notifyNotes || null,
        notifiedAt: `${notifyAt}:00Z`,
      });
      setDetail((prev) => (prev ? { ...prev, incident: updated } : null));
      setShowNotifyPanel(false);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setNotifying(false);
    }
  }

  async function handleSaveEdit(e: React.FormEvent) {
    e.preventDefault();
    if (!id || !incident) return;
    setSaving(true);
    try {
      const patch: UpdateIncidentInput = {
        actionTaken: editActionTaken || null,
        followUpNotes: editFollowUpNotes || null,
      };
      const updated = await updateIncident(id, patch);
      setDetail((prev) => (prev ? { ...prev, incident: updated } : null));
      setEditMode(false);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <div className="p-8 text-sm text-muted-foreground">Loading…</div>;
  if (!incident) return <div className="p-8 text-sm text-destructive">{error ?? 'Not found'}</div>;

  const isOpen = incident.status === 'open';

  return (
    <div className="min-h-[100dvh]">
      <div className="container mx-auto max-w-3xl px-4 py-8">
        <nav className="mb-6 text-sm text-muted-foreground">
          <Link to="/incidents" className="hover:underline">
            Incidents
          </Link>
          {' / '}
          <span className="text-foreground capitalize">
            {incident.incidentType.replace(/_/g, ' ')}
          </span>
        </nav>

        {error && (
          <div className="rounded-lg bg-destructive/10 text-destructive px-4 py-3 mb-4 text-sm">
            {error}
          </div>
        )}

        <header className="flex items-start justify-between mb-6">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <h1 className="text-2xl font-semibold capitalize">
                {incident.incidentType.replace(/_/g, ' ')}
              </h1>
              <span
                className={`rounded-full px-2 py-0.5 text-xs font-medium capitalize ${SEVERITY_COLORS[incident.severity] ?? ''}`}
              >
                {incident.severity}
              </span>
              <span className="rounded-full bg-secondary px-2 py-0.5 text-xs capitalize">
                {incident.status}
              </span>
            </div>
            <p className="text-sm text-muted-foreground">
              Occurred {formatDatetime(incident.occurredAt)}
            </p>
          </div>

          {isOpen && (
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setShowNotifyPanel(true)}
                className="rounded-md border border-border px-3 py-1.5 text-sm hover:bg-secondary transition-colors"
              >
                Record owner notification
              </button>
              <button
                type="button"
                onClick={() => void handleResolve()}
                disabled={resolving}
                className="rounded-md bg-green-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-green-700 transition-colors disabled:opacity-50"
              >
                {resolving ? 'Resolving…' : 'Mark resolved'}
              </button>
            </div>
          )}
        </header>

        {/* Main details */}
        <div className="space-y-6">
          <section className="rounded-lg border border-border p-4">
            <h2 className="text-sm font-semibold mb-3">What happened</h2>
            <p className="text-sm whitespace-pre-wrap">{incident.description}</p>
          </section>

          <section className="rounded-lg border border-border p-4">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold">Action taken</h2>
              {isOpen && !editMode && (
                <button
                  type="button"
                  onClick={() => setEditMode(true)}
                  className="text-xs text-primary hover:underline"
                >
                  Edit
                </button>
              )}
            </div>

            {editMode ? (
              <form onSubmit={(e) => void handleSaveEdit(e)} className="space-y-3">
                <div>
                  <label className="block text-xs font-medium mb-1">Action taken</label>
                  <textarea
                    value={editActionTaken}
                    onChange={(e) => setEditActionTaken(e.target.value)}
                    rows={3}
                    className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm resize-none"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">Follow-up notes</label>
                  <textarea
                    value={editFollowUpNotes}
                    onChange={(e) => setEditFollowUpNotes(e.target.value)}
                    rows={2}
                    className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm resize-none"
                  />
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setEditMode(false)}
                    className="rounded-md border border-border px-3 py-1.5 text-xs hover:bg-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className="rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground disabled:opacity-50"
                  >
                    {saving ? 'Saving…' : 'Save'}
                  </button>
                </div>
              </form>
            ) : (
              <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                {incident.actionTaken ?? '—'}
              </p>
            )}
          </section>

          {/* Owner notification */}
          <section className="rounded-lg border border-border p-4">
            <h2 className="text-sm font-semibold mb-3">Owner notification</h2>
            {incident.ownerNotifiedAt ? (
              <div className="text-sm space-y-1">
                <p>
                  <span className="font-medium">Notified:</span>{' '}
                  {formatDatetime(incident.ownerNotifiedAt)}
                </p>
                <p>
                  <span className="font-medium">Method:</span>{' '}
                  <span className="capitalize">
                    {incident.ownerNotificationMethod?.replace(/_/g, ' ') ?? '—'}
                  </span>
                </p>
                {incident.ownerNotificationNotes && (
                  <p>
                    <span className="font-medium">Notes:</span> {incident.ownerNotificationNotes}
                  </p>
                )}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">Not yet notified.</p>
            )}
          </section>

          {/* Follow-up */}
          {incident.followUpRequired === 1 && (
            <section className="rounded-lg border border-amber-200 bg-amber-50 p-4">
              <h2 className="text-sm font-semibold text-amber-900 mb-2">Follow-up required</h2>
              <div className="text-sm text-amber-800 space-y-1">
                {incident.followUpDueOn && (
                  <p>
                    Due: <span className="font-medium">{incident.followUpDueOn}</span>
                  </p>
                )}
                {incident.followUpNotes && <p>{incident.followUpNotes}</p>}
              </div>
            </section>
          )}

          {/* Resolution */}
          {incident.resolvedAt && (
            <section className="rounded-lg border border-green-200 bg-green-50 p-4">
              <h2 className="text-sm font-semibold text-green-900 mb-1">Resolved</h2>
              <p className="text-sm text-green-800">
                Resolved on {formatDatetime(incident.resolvedAt)}
              </p>
            </section>
          )}

          {/* Meta */}
          <section className="text-xs text-muted-foreground space-y-1 pt-2 border-t border-border">
            <p>ID: {incident.id}</p>
            <p>Pet: {detail?.petName ?? incident.petId}</p>
            {detail?.customerName && <p>Owner: {detail.customerName}</p>}
            {incident.bookingId && <p>Booking: {incident.bookingId.slice(-8).toUpperCase()}</p>}
            <p>Created: {formatDatetime(incident.createdAt)}</p>
            <p>Updated: {formatDatetime(incident.updatedAt)}</p>
          </section>
        </div>

        {/* Notify owner panel */}
        {showNotifyPanel && (
          <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40">
            <div className="bg-background rounded-t-2xl sm:rounded-2xl shadow-xl p-6 w-full sm:max-w-md mx-4">
              <h2 className="text-lg font-semibold mb-4">Record owner notification</h2>
              <form onSubmit={(e) => void handleNotifyOwner(e)} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Method</label>
                    <select
                      value={notifyMethod}
                      onChange={(e) => setNotifyMethod(e.target.value as OwnerNotificationMethod)}
                      className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                    >
                      {OWNER_NOTIFICATION_METHODS.map((m) => (
                        <option key={m} value={m}>
                          {m.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">When</label>
                    <input
                      type="datetime-local"
                      value={notifyAt}
                      onChange={(e) => setNotifyAt(e.target.value)}
                      className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Notes</label>
                  <textarea
                    value={notifyNotes}
                    onChange={(e) => setNotifyNotes(e.target.value)}
                    rows={3}
                    className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm resize-none"
                  />
                </div>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setShowNotifyPanel(false)}
                    className="flex-1 rounded-md border border-border px-4 py-2 text-sm"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={notifying}
                    className="flex-1 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50"
                  >
                    {notifying ? 'Saving…' : 'Save'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
